import React, { useState, useEffect } from 'react';
import {
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
} from '@floating-ui/react';
import Select from './Select.jsx';
import NumberInput from './NumberInput.jsx';
import { DateTextInput } from './DateTextInput.jsx';
import { fmtMoney } from '../engine.js';
import { currencySymbol, todayISO } from '../formatting.js';

const PM_BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed';
const PM_BTN_PRIMARY = 'bg-[rgba(240,180,41,0.10)] border border-accent-gold text-accent-gold px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:bg-[rgba(240,180,41,0.18)]';
const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const LABEL_WRAP = 'flex flex-col gap-[0.3rem] py-[0.45rem]';
const LABEL_TEXT = 'text-[0.82rem] text-text';
const FIELD_WRAP = 'flex items-center bg-surface-raised border border-border-strong rounded-sm overflow-hidden focus-within:border-accent-blue';
const FIELD_INPUT = 'bg-transparent text-text border-0 px-[7px] py-1 w-full font-[inherit] text-[0.85rem] focus:outline-none';

/* AssetTransactionModal — sell or buy a fixed asset. */
export default function AssetTransactionModal({ open, onClose, mode, asset, ltl, onApply }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    if (mode === 'sell') {
      setForm({
        salePrice: asset?.currentValue ?? 0,
        closingDate: todayISO(),
        fees: 0,
        ltlPayoff: ltl?.outstanding ?? 0,
      });
    } else if (mode === 'buy') {
      setForm({
        assetName: '',
        category: 'Real Estate',
        purchasePrice: 0,
        downPaymentPct: 20,
        closingDate: todayISO(),
        financingSource: 'new-ltl',
        interestRate: 7.5,
        termMonths: 240,
      });
    } else if (mode === 'ltl-payoff') {
      setForm({
        ltlId: ltl?.id,
        ltlName: ltl?.name,
        payoffAmount: ltl?.outstanding ?? 0,
        payoffDate: todayISO(),
        earlyFee: 0,
      });
    } else if (mode === 'ltl-new') {
      setForm({
        name: '',
        outstanding: 0,
        interestRate: 7.5,
        termMonths: 60,
        source: 'bank-loan',
        startDate: todayISO(),
      });
    }
  }, [open, mode, asset, ltl]);

  const { refs, context } = useFloating({
    open,
    onOpenChange: (o) => { if (!o) onClose(); },
  });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  if (!open) return null;

  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const moneyPrefix = currencySymbol();

  let preview = null;
  if (mode === 'sell') {
    const grossSale   = Number(form.salePrice || 0);
    const fees        = Number(form.fees || 0);
    const ltlPayoff   = Number(form.ltlPayoff || 0);
    const bookValue   = (asset?.currentValue ?? 0) - (asset?.accumDepreciation ?? 0);
    const netCash     = grossSale - fees - ltlPayoff;
    const gainLoss    = grossSale - fees - bookValue;
    preview = { grossSale, fees, ltlPayoff, bookValue, netCash, gainLoss };
  } else if (mode === 'buy') {
    const price       = Number(form.purchasePrice || 0);
    const dpPct       = Number(form.downPaymentPct || 0);
    const downPayment = price * (dpPct / 100);
    const financed    = price - downPayment;
    const months      = Math.max(1, Number(form.termMonths || 240));
    const rate        = Math.max(0, Number(form.interestRate || 0)) / 100;
    const r           = rate / 12;
    const monthlyPayment = r === 0
      ? financed / months
      : (financed * r) / (1 - Math.pow(1 + r, -months));
    preview = { price, downPayment, financed, monthlyPayment, months };
  } else if (mode === 'ltl-payoff') {
    const amt = Number(form.payoffAmount || 0);
    const fee = Number(form.earlyFee || 0);
    preview = { payoffAmount: amt, earlyFee: fee, netCash: -(amt + fee) };
  } else if (mode === 'ltl-new') {
    const principal = Number(form.outstanding || 0);
    const months    = Math.max(1, Number(form.termMonths || 60));
    const rate      = Math.max(0, Number(form.interestRate || 0)) / 100;
    const r         = rate / 12;
    const monthlyPayment = r === 0
      ? principal / months
      : (principal * r) / (1 - Math.pow(1 + r, -months));
    preview = { principal, months, monthlyPayment };
  }

  const handleApply = () => {
    const id = `txn-${Date.now().toString(36)}`;
    if (mode === 'sell') {
      const txn = {
        id,
        mode: 'sell',
        assetId: asset?.id,
        assetName: asset?.name,
        linkedLTLId: ltl?.id ?? asset?.liabilityLink,
        salePrice: Number(form.salePrice || 0),
        closingDate: form.closingDate,
        fees: Number(form.fees || 0),
        ltlPayoff: Number(form.ltlPayoff || 0),
        bookValue: preview.bookValue,
        netCash: preview.netCash,
        gainLoss: preview.gainLoss,
        appliedAt: new Date().toISOString(),
      };
      onApply(txn);
    } else if (mode === 'buy') {
      const txn = {
        id,
        mode: 'buy',
        assetName: form.assetName || 'Untitled purchase',
        category: form.category,
        purchasePrice: Number(form.purchasePrice || 0),
        downPayment: preview.downPayment,
        financed: preview.financed,
        monthlyPayment: preview.monthlyPayment,
        interestRate: Number(form.interestRate || 0) / 100,
        amortizeMonths: Number(form.termMonths || 240),
        closingDate: form.closingDate,
        createLTL: form.financingSource === 'new-ltl',
        netCash: -(preview.downPayment),
        appliedAt: new Date().toISOString(),
      };
      onApply(txn);
    } else if (mode === 'ltl-payoff') {
      onApply({
        id,
        mode: 'ltl-payoff',
        ltlId: form.ltlId,
        ltlName: form.ltlName,
        payoffAmount: Number(form.payoffAmount || 0),
        earlyFee: Number(form.earlyFee || 0),
        payoffDate: form.payoffDate,
        netCash: preview.netCash,
        appliedAt: new Date().toISOString(),
      });
    } else if (mode === 'ltl-new') {
      onApply({
        id,
        mode: 'ltl-new',
        name: form.name || 'Untitled LTL',
        outstanding: Number(form.outstanding || 0),
        interestRate: Number(form.interestRate || 0) / 100,
        termMonths: Number(form.termMonths || 60),
        monthlyPayment: preview.monthlyPayment,
        source: form.source,
        startDate: form.startDate,
        netCash: Number(form.outstanding || 0),  /* cash in on loan proceeds */
        appliedAt: new Date().toISOString(),
      });
    }
  };

  return (
    <FloatingPortal>
    <FloatingOverlay lockScroll className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/55 px-4 py-4 backdrop-blur-[4px] sm:py-8">
    <FloatingFocusManager context={context}>
      <div
        ref={refs.setFloating}
        {...getFloatingProps()}
        className="flex w-full min-w-0 max-w-[720px] max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border border-border-strong bg-surface-el shadow-lg outline-none"
        aria-label={`${mode} asset`}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 px-[1.1rem] py-[0.85rem] border-b border-border">
          <h3 className="text-base font-semibold text-text m-0">
            {mode === 'sell' && `Sell: ${asset?.name}`}
            {mode === 'buy' && 'Buy asset'}
            {mode === 'ltl-payoff' && `Pay off LTL: ${ltl?.name}`}
            {mode === 'ltl-new' && 'Add new long-term liability'}
          </h3>
          <button type="button" className={BTN_ICON} onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain px-[1.1rem] pt-4 pb-[1.2rem]">
          {mode === 'sell' && (
            <>
              <div className="grid grid-cols-1 gap-x-[0.8rem] gap-y-[0.6rem] mb-[0.85rem] sm:grid-cols-2">
                <NumberInput
                  label="Sale price"
                  prefix={moneyPrefix}
                  value={form.salePrice ?? 0}
                  onChange={(v) => update({ salePrice: v })}
                  decimals={2}
                />
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Closing date</span>
                  <span className={FIELD_WRAP}>
                    <DateTextInput className={FIELD_INPUT} value={form.closingDate ?? ''} onChange={(e) => update({ closingDate: e.target.value })} />
                  </span>
                </label>
                <NumberInput
                  label="Fees"
                  prefix={moneyPrefix}
                  value={form.fees ?? 0}
                  onChange={(v) => update({ fees: v })}
                  decimals={2}
                />
                {ltl && (
                  <NumberInput
                    label="Liability payoff"
                    prefix={moneyPrefix}
                    value={form.ltlPayoff ?? 0}
                    onChange={(v) => update({ ltlPayoff: v })}
                    decimals={2}
                  />
                )}
              </div>
              <div className="bg-surface-raised border border-dashed border-accent-gold rounded-sm px-[0.8rem] py-[0.6rem]">
                <h4 className="m-0 mb-[0.4rem] text-[0.8rem] uppercase tracking-[0.06em] text-accent-gold">Preview</h4>
                <ul className="list-none p-0 m-0">
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Gross sale</span><strong className="tabular-nums">{fmtMoney(preview.grossSale, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>− Fees</span><strong className="tabular-nums">{fmtMoney(preview.fees, { decimals: 2 })}</strong></li>
                  {ltl && <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>− Liability payoff ({ltl.name.replace('LTL: ', '')})</span><strong className="tabular-nums">{fmtMoney(preview.ltlPayoff, { decimals: 2 })}</strong></li>}
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border pt-[0.4rem] !border-t !border-t-border-strong font-semibold"><span>= Net cash to you</span><strong className={`tabular-nums ${preview.netCash >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>{fmtMoney(preview.netCash, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] last:border-b-0"><span>Gain/(Loss) vs book ({fmtMoney(preview.bookValue, { decimals: 2 })})</span><strong className={`tabular-nums ${preview.gainLoss >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>{fmtMoney(preview.gainLoss, { decimals: 2 })}</strong></li>
                </ul>
              </div>
            </>
          )}
          {mode === 'buy' && (
            <>
              <div className="grid grid-cols-1 gap-x-[0.8rem] gap-y-[0.6rem] mb-[0.85rem] sm:grid-cols-2">
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Asset name</span>
                  <span className={FIELD_WRAP}>
                    <input type="text" className={FIELD_INPUT} value={form.assetName ?? ''} onChange={(e) => update({ assetName: e.target.value })} placeholder="e.g. Honda Click 160" />
                  </span>
                </label>
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Category</span>
                  <Select className="!flex w-full" value={form.category ?? 'Real Estate'} onChange={(e) => update({ category: e.target.value })}>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Vehicle">Vehicle</option>
                    <option value="Software">Software (lifetime)</option>
                    <option value="Other">Other</option>
                  </Select>
                </label>
                <NumberInput
                  label="Purchase price"
                  prefix={moneyPrefix}
                  value={form.purchasePrice ?? 0}
                  onChange={(v) => update({ purchasePrice: v })}
                  decimals={2}
                />
                <NumberInput
                  label="Down payment %"
                  suffix="%"
                  value={form.downPaymentPct ?? 20}
                  onChange={(v) => update({ downPaymentPct: v })}
                  decimals={2}
                  min={0}
                  max={100}
                />

                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Financing</span>
                  <Select className="!flex w-full" value={form.financingSource ?? 'new-ltl'} onChange={(e) => update({ financingSource: e.target.value })}>
                    <option value="cash">Pay cash (no LTL)</option>
                    <option value="new-ltl">New long-term liability</option>
                  </Select>
                </label>
                {form.financingSource === 'new-ltl' && (
                  <>
                    <NumberInput
                      label="Interest rate"
                      suffix="%"
                      value={form.interestRate ?? 7.5}
                      onChange={(v) => update({ interestRate: v })}
                      decimals={2}
                      min={0}
                    />
                    <NumberInput
                      label="Term (months)"
                      value={form.termMonths ?? 240}
                      onChange={(v) => update({ termMonths: Math.round(v) })}
                      decimals={2}
                      min={1}
                    />
                  </>
                )}
              </div>
              <div className="bg-surface-raised border border-dashed border-accent-gold rounded-sm px-[0.8rem] py-[0.6rem]">
                <h4 className="m-0 mb-[0.4rem] text-[0.8rem] uppercase tracking-[0.06em] text-accent-gold">Preview</h4>
                <ul className="list-none p-0 m-0">
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Purchase price</span><strong className="tabular-nums">{fmtMoney(preview.price, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>− Down payment ({form.downPaymentPct}%)</span><strong className="tabular-nums">{fmtMoney(preview.downPayment, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>= Financed</span><strong className="tabular-nums">{fmtMoney(preview.financed, { decimals: 2 })}</strong></li>
                  {form.financingSource === 'new-ltl' && (
                    <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border pt-[0.4rem] !border-t !border-t-border-strong font-semibold"><span>Monthly payment ({preview.months}mo @ {form.interestRate}%)</span><strong className="tabular-nums text-accent-red">{fmtMoney(preview.monthlyPayment, { decimals: 2 })}</strong></li>
                  )}
                  <li className="flex justify-between py-1 text-[0.85rem] last:border-b-0"><span>Cash out of selected cash account today</span><strong className="tabular-nums text-accent-red">{fmtMoney(-preview.downPayment, { decimals: 2 })}</strong></li>
                </ul>
              </div>
            </>
          )}

          {mode === 'ltl-payoff' && (
            <>
              <div className="grid grid-cols-1 gap-x-[0.8rem] gap-y-[0.6rem] mb-[0.85rem] sm:grid-cols-2">
                <NumberInput
                  label="Payoff amount"
                  prefix={moneyPrefix}
                  value={form.payoffAmount ?? 0}
                  onChange={(v) => update({ payoffAmount: v })}
                  decimals={2}
                  min={0}
                />
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Payoff date</span>
                  <span className={FIELD_WRAP}>
                    <DateTextInput className={FIELD_INPUT} value={form.payoffDate ?? ''} onChange={(e) => update({ payoffDate: e.target.value })} />
                  </span>
                </label>
                <NumberInput
                  label="Early-payoff fee"
                  prefix={moneyPrefix}
                  value={form.earlyFee ?? 0}
                  onChange={(v) => update({ earlyFee: v })}
                  decimals={2}
                  min={0}
                  hint="If any"
                />
              </div>
              <div className="bg-surface-raised border border-dashed border-accent-gold rounded-sm px-[0.8rem] py-[0.6rem]">
                <h4 className="m-0 mb-[0.4rem] text-[0.8rem] uppercase tracking-[0.06em] text-accent-gold">Preview</h4>
                <ul className="list-none p-0 m-0">
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Liability</span><strong>{form.ltlName}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Payoff amount</span><strong className="tabular-nums">{fmtMoney(preview.payoffAmount, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>+ Early-payoff fee</span><strong className="tabular-nums">{fmtMoney(preview.earlyFee, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] last:border-b-0 pt-[0.4rem] !border-t !border-t-border-strong font-semibold"><span>Cash out of selected cash account today</span><strong className="tabular-nums text-accent-red">{fmtMoney(preview.netCash, { decimals: 2 })}</strong></li>
                </ul>
              </div>
            </>
          )}

          {mode === 'ltl-new' && (
            <>
              <div className="grid grid-cols-1 gap-x-[0.8rem] gap-y-[0.6rem] mb-[0.85rem] sm:grid-cols-2">
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Liability name</span>
                  <span className={FIELD_WRAP}>
                    <input type="text" className={FIELD_INPUT} value={form.name ?? ''} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. BDO auto loan" />
                  </span>
                </label>
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Source</span>
                  <Select className="!flex w-full" value={form.source ?? 'bank-loan'} onChange={(e) => update({ source: e.target.value })}>
                    <option value="bank-loan">Bank loan</option>
                    <option value="private-loan">Private loan</option>
                    <option value="housing-fund">Housing fund loan</option>
                    <option value="other">Other</option>
                  </Select>
                </label>
                <NumberInput
                  label="Principal (new cash in)"
                  prefix={moneyPrefix}
                  value={form.outstanding ?? 0}
                  onChange={(v) => update({ outstanding: v })}
                  decimals={2}
                  min={0}
                />
                <NumberInput
                  label="Interest rate"
                  suffix="%"
                  value={form.interestRate ?? 7.5}
                  onChange={(v) => update({ interestRate: v })}
                  decimals={2}
                  min={0}
                />
                <NumberInput
                  label="Term (months)"
                  value={form.termMonths ?? 60}
                  onChange={(v) => update({ termMonths: Math.round(v) })}
                  decimals={2}
                  min={1}
                />
                <label className={LABEL_WRAP}>
                  <span className={LABEL_TEXT}>Start date</span>
                  <span className={FIELD_WRAP}>
                    <DateTextInput className={FIELD_INPUT} value={form.startDate ?? ''} onChange={(e) => update({ startDate: e.target.value })} />
                  </span>
                </label>
              </div>
              <div className="bg-surface-raised border border-dashed border-accent-gold rounded-sm px-[0.8rem] py-[0.6rem]">
                <h4 className="m-0 mb-[0.4rem] text-[0.8rem] uppercase tracking-[0.06em] text-accent-gold">Preview</h4>
                <ul className="list-none p-0 m-0">
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Principal</span><strong className="tabular-nums">{fmtMoney(preview.principal, { decimals: 2 })}</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] border-b border-dashed border-border"><span>Term</span><strong>{preview.months} months</strong></li>
                  <li className="flex justify-between py-1 text-[0.85rem] last:border-b-0 pt-[0.4rem] !border-t !border-t-border-strong font-semibold"><span>Monthly payment</span><strong className="tabular-nums text-accent-red">{fmtMoney(preview.monthlyPayment, { decimals: 2 })}</strong></li>
                </ul>
              </div>
            </>
          )}
        </div>
        <footer className="flex shrink-0 flex-wrap justify-end gap-2 px-[1.1rem] py-3 border-t border-border">
          <button type="button" className={PM_BTN} onClick={onClose}>Cancel</button>
          <button type="button" className={PM_BTN_PRIMARY} onClick={handleApply}>
            Apply {mode === 'sell' ? 'sale' : 'purchase'}
          </button>
        </footer>
      </div>
    </FloatingFocusManager>
    </FloatingOverlay>
    </FloatingPortal>
  );
}

